import { CLOUD_UNUSED_WINDOW_MS } from "@afar-research/contracts";

/** Whether the page has gone unused, and if not, when it next could have. */
export function unusedPageCheck(input: {
  readonly lastUsedAt: number;
  readonly now: number;
}): { readonly unused: true } | { readonly unused: false; readonly checkInMs: number } {
  const remaining = input.lastUsedAt + CLOUD_UNUSED_WINDOW_MS - input.now;
  return remaining <= 0 ? { unused: true } : { unused: false, checkInMs: remaining };
}

/**
 * Whether this page wants its Cloud Workspace connection. An unused page keeps an open connection,
 * telling the Environment it is away so the run can stop once its agent is idle too, and the user
 * who comes back to a run still up just carries on. It never opens a new one: connecting starts a
 * stopped workspace, which nobody is there to use. The next use connects again.
 */
export function cloudWorkspaceConnectionWanted(input: {
  readonly selected: boolean;
  readonly unused: boolean;
  readonly connected: boolean;
}): boolean {
  return input.selected && (!input.unused || input.connected);
}

/**
 * What to tell a connected Cloud Workspace about this page, or nothing. A new connection starts
 * present, so only a change is news. An Environment that does not advertise the capability fails
 * the whole connection on a request it does not know, so it is never asked.
 */
export function cloudClientAwayToReport(input: {
  readonly connected: boolean;
  readonly supported: boolean;
  readonly unused: boolean;
  /** What this connection was last told; false for a new one. */
  readonly reported: boolean;
}): boolean | null {
  if (!input.connected || !input.supported || input.unused === input.reported) return null;
  return input.unused;
}
